import React from "react";
import { Calendar, ChevronDown, ArrowRight } from "lucide-react";
import "./RedemptionsTab.css";

const summaryStats = [
  { label: "Total Redemption", value: "1,284" },
  { label: "Unique Users", value: "982" },
  { label: "Average Order Value", value: "131 EGP" },
  { label: "Total Discounted Amount", value: "15,000 EGP" },
];

const totalSales = { label: "Total Sales Generated", value: "120,000 EGP" };

const chartLabels = [
  "Mar 10",
  "Mar 17",
  "Mar 24",
  "Mar 31",
  "Apr 07",
  "Apr 21",
  "Apr 28",
  "May 05",
  "May 12",
  "May 19",
  "May 26",
  "May 31",
];

const chartValues = [20, 65, 110, 140, 165, 140, 120, 110, 150, 185, 170, 205];

const chartDots = [0, 1, 2, 4, 7, 9, 10, 11]; 

const statusData = [
  { label: "Completed", count: 1152, pct: "89.6%", color: "#14B8A6" },
  { label: "Failed", count: 64, pct: "5.0%", color: "#F5A623" },
  { label: "Expired", count: 48, pct: "3.7%", color: "#4B5563" },
  { label: "Cancelled", count: 20, pct: "1.6%", color: "#DC2626" },
];

const statusTotal = 256;

const channelData = [
  { label: "Mobile App", count: 912, pct: "71.0%" },
  { label: "Website", count: 232, pct: "18.1%" },
  { label: "Call Center", count: 96, pct: "7.5%" },
  { label: "POS", count: 44, pct: "3.4%" },
];

const topUsers = [
  { name: "Osama Ali", count: 24 },
  { name: "Ahmed Hassan", count: 18 },
  { name: "Amr Hassan", count: 17 },
  { name: "Mona Salah", count: 15 },
];

const insights = [
  "Redemption rate is 12% higher than average for similar coupons.",
  "Mobile App is the top channel with 71% of total redemptions.",
  "Most redemptions occur on weekends.",
];

const tableRows = Array.from({ length: 7 }).map((_, i) => ({
  id: "RED-1248",
  user: "Mona Salah",
  date: "May 18, 2025 - 02:15 pm",
  orderId: "ORD-3549",
  orderValue: "420 EGP",
  discountValue: "80 EGP",
  status: "Completed",
  channel: "Mobile App",
}));

const avatarColors = ["#FDE68A", "#BFDBFE", "#FBCFE8", "#C7D2FE"];

function StepBadge({ n }) {

  return <span className="rt-step-badge">{n}</span>;
}

function LineChart({ labels, values, dots }) {
  const width = 1000;
  const height = 260;
  const padLeft = 40;
  const padBottom = 30;
  const maxY = 250;
  const gridValues = [0, 50, 100, 150, 200, 250];

  const plotW = width - padLeft;
  const plotH = height - padBottom;
  const stepX = plotW / (labels.length - 1);

  const points = values.map((v, i) => ({
    x: padLeft + i * stepX,
    y: plotH - (v / maxY) * plotH,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${plotH} L ${points[0].x} ${plotH} Z`;

  return (
    <div className="rt-chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height + 20}`}
        className="rt-chart-svg"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="rtAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid lines + Y labels */}
        {gridValues.map((g) => {
          const y = plotH - (g / maxY) * plotH;
          return (
            <g key={g}>
              <line
                x1={padLeft}
                x2={width}
                y1={y}
                y2={y}
                className="rt-grid-line"
              />
              <text
                x={padLeft - 10}
                y={y + 4}
                className="rt-axis-label"
                textAnchor="end"
              >
                {g}
              </text>
            </g>
          );
        })}

        {/* Area + Line */}
        <path d={areaPath} fill="url(#rtAreaFill)" />
        <path d={linePath} className="rt-line-path" />

        {/* Dots */}
        {points.map(
          (p, i) =>
            dots.includes(i) && (
              <circle key={i} cx={p.x} cy={p.y} r="5" className="rt-line-dot" />
            ),
        )}

        {/* X labels */}
        {labels.map((l, i) => (
          <text
            key={l}
            x={padLeft + i * stepX}
            y={plotH + 24}
            className="rt-axis-label"
            textAnchor="middle"
          >
            {l}
          </text>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({ data, total }) {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const r = 70;
  const strokeWidth = 22;
  const circumference = 2 * Math.PI * r;

  let cumulative = 0;
  const segments = data.map((d) => {
    const pct = parseFloat(d.pct) / 100;
    const length = pct * circumference;
    const offset = cumulative;
    cumulative += length;
    return { ...d, length, offset };
  });

  return (
    <div className="rt-donut-chart">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {segments.map((s, i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${s.length} ${circumference - s.length}`}
              strokeDashoffset={-s.offset}
            />
          ))}
        </g>
      </svg>
      <div className="rt-donut-center">
        <span className="rt-donut-total">{total}</span>
        <span className="rt-donut-total-label">Total</span>
      </div>
    </div>
  );
}

export default function RedemptionsTab() {
  return (
    <div className="rt-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="rt-main">
        <div className="rt-panel">
          {/* 1. Redemption Summary */}
          <div className="rt-panel-section">
            <div className="rt-card-header">
              <StepBadge n={1} />
              <h3 className="rt-card-title">Redemption Summary</h3>
            </div>

            <div className="rt-summary-grid">
              {summaryStats.map((s) => (
                <div className="rt-summary-item" key={s.label}>
                  <span className="rt-summary-label">{s.label}</span>
                  <span className="rt-summary-value">{s.value}</span>
                </div>
              ))}
            </div>

            <div className="rt-summary-item mt-2">
              <span className="rt-summary-label">{totalSales.label}</span>
              <span className="rt-summary-value">{totalSales.value}</span>
            </div>
          </div>

          <div className="rt-panel-divider" />

          {/* 2. Redemption Over Time */}
          <div className="rt-panel-section">
            <div className="rt-card-header-row">
              <div className="rt-card-header">
                <StepBadge n={2} />
                <h3 className="rt-card-title">Redemption Over Time</h3>
              </div>

              <div className="rt-filters">
                <button className="rt-select-btn">
                  Daily <ChevronDown size={14} />
                </button>
                <button className="rt-date-btn">
                  <Calendar size={14} />
                  Mar 10, 2025 - May 31, 2025
                </button>
              </div>
            </div>

            <LineChart
              labels={chartLabels}
              values={chartValues}
              dots={chartDots}
            />
          </div>

          <div className="rt-panel-divider" />

          {/* 3. Recent Redemptions */}
          <div className="rt-panel-section">
            <div className="rt-card-header-row">
              <div className="rt-card-header">
                <StepBadge n={3} />
                <h3 className="rt-card-title">Recent Redemptions</h3>
              </div>
              <a className="rt-link" href="#">
                View all <ArrowRight size={14} />
              </a>
            </div>

            <div className="rt-table-wrap">
              <table className="rt-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Date & Time</th>
                    <th>Order ID</th>
                    <th>Order Value</th>
                    <th>Discount Value</th>
                    <th>Status</th>
                    <th>Channel</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.id}</td>
                      <td>{row.user}</td>
                      <td>{row.date}</td>
                      <td>{row.orderId}</td>
                      <td>{row.orderValue}</td>
                      <td>{row.discountValue}</td>
                      <td>
                        <span
                          className={`rt-status-pill rt-status-${row.status.toLowerCase()}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>{row.channel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- SIDEBAR ---------- */}
      <div className="rt-sidebar">
        {/* Redemption by Status */}
        <div className="rt-card pb-0">
          <h3 className="rt-rg-card-title">Redemption by Status</h3>
          <div className="rt-status-row py-2">
            <DonutChart data={statusData} total={statusTotal} />
            <div className="rt-status-legend">
              {statusData.map((s) => (
                <div className="rt-legend-item" key={s.label}>
                  <span
                    className="rt-legend-dot"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="rt-legend-label">
                    {s.label} ({s.count})
                  </span>
                  <span className="rt-legend-pct">{s.pct}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Redemption by Channel */}
        <div className="rt-card pb-0">
          <h3 className="rt-rg-card-title">Redemption by Channel</h3>
          <div className="rt-channel-list py-3">
            {channelData.map((c, i) => (
              <div className="rt-channel-item" key={c.label}>
                <span className="rt-channel-num">{i + 1}</span>
                <span className="rt-channel-label">{c.label}</span>
                <span className="rt-channel-count">{c.count}</span>
                <span className="rt-channel-pct">{c.pct}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Redeeming Users */}
        <div className="rt-card pb-0">
          <div className="rt-rg-card-title">
            <h3 >Top Redeeming Users</h3>
            <span className="rt-link">
              View Full Report <ArrowRight size={14} />
            </span>
          </div>
          <div className="rt-users-list py-3">
            {topUsers.map((u, i) => (
              <div className="rt-user-item" key={u.name}>
                <span
                  className="rt-user-avatar"
                  style={{
                    backgroundColor: avatarColors[i % avatarColors.length],
                  }}
                >
                  {u.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </span>
                <span className="rt-user-name">{u.name}</span>
                <span className="rt-user-count">{u.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Insights */}
        <div className="rt-card pb-0">
          <div className="rt-rg-card-title">
            <h3 >Insights</h3>
            <span className="rt-link" >
              View More Insights <ArrowRight size={14} />
            </span>
          </div>
         <div className="rt-insights-list py-3">
  {insights.map((text, i) => (
    <div key={i} className="rt-insight-item">
      <span className="rt-insight-dot" />
      <span className="rt-insight-text">{text}</span>
      </div>
         ))}
        </div>
        </div>
      </div>
    </div>
  );
}
